import { Target, Users, Calendar, Award, BookOpen, Globe } from 'lucide-react';

export default function AboutPage() {
  const features = [
    {
      icon: Target,
      title: 'Миссия',
      description: 'Развитие креативных индустрий в Югре через выявление и поддержку талантливой молодёжи.',
    },
    {
      icon: Users,
      title: 'Участники',
      description: 'Школьники 8–11 классов и студенты 1–2 курсов СПО (14–17 лет) со всей России.',
    },
    {
      icon: Calendar,
      title: 'Формат',
      description: 'Онлайн-отбор и очно-заочный финал с менторской поддержкой и мастер-классами.',
    },
    {
      icon: Award,
      title: 'Призы',
      description: 'Гранты, стажировки в компаниях-партнёрах, сертификаты, оборудование для творчества.',
    },
    {
      icon: BookOpen,
      title: 'Обучение',
      description: 'Бесплатные образовательные модули от экспертов индустрии на всех этапах.',
    },
    {
      icon: Globe,
      title: 'Партнёры',
      description: 'Ведущие студии геймдева, IT-компании, образовательные платформы и органы власти.',
    },
  ];

  const rules = [
    'Участник должен быть в возрасте от 14 до 18 лет на момент старта чемпионата.',
    'Индивидуальное участие на отборочном этапе.',
    'На финальном этапе формируются команды по 3–5 человек.',
    'Участник может выбрать только одно направление (компетенцию).',
    'Все работы должны быть оригинальными, созданными в период проведения чемпионата.',
    'Организаторы оставляют право использовать работы в некоммерческих целях.',
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">О чемпионате</h1>
        <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
          ОЧКИ — это уникальная площадка для школьников и студентов, где креативные идеи
          превращаются в реальные проекты под руководством профессионалов индустрии.
        </p>
      </div>

      <div className="card bg-gradient-to-br from-primary-50 to-accent-50 border-l-4 border-primary-500 mb-16 max-w-4xl mx-auto">
        <div className="space-y-4 text-gray-700 leading-relaxed">
          <p>
            В 2026 году состоится знаменательное событие — пятый, юбилейный Региональный
            онлайн-чемпионат Югры по креативным индустриям. Пять лет чемпионат открывает
            молодым талантам Югры дорогу в мир творчества, технологий и профессионального
            роста, объединяя школьников и студентов вокруг идей креативной экономики.
          </p>
          <p>
            В этот торжественный год чемпионат вновь включён в перечень олимпиад и иных
            интеллектуальных и (или) творческих конкурсов, мероприятий, направленных на
            развитие интеллектуальных и творческих способностей, на 2026/27 учебный год,
            утверждённый приказом Министерства просвещения Российской Федерации от 28
            сентября 2026 г. № 598. Это уже не первый год, когда проект получает такое
            признание: включение в перечень Минпросвещения подтверждает его высокий статус,
            устойчивую репутацию и значимый вклад в развитие талантов нового поколения.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="prose prose-lg max-w-none">
            <h2>Что такое ОЧКИ?</h2>
            <p>
              <strong>Онлайн-чемпионат Югры по креативным индустриям (ОЧКИ)</strong> — это
              ежегодное соревнование, созданное АНО ДПО «Институт развития компетенций» при
              поддержке Правительства Ханты-Мансийского автономного округа — Югры.
            </p>
            <p>
              Чемпионат направлен на развитие навыков будущего у подростков и молодёжи в
              сферах геймдева, 3D-моделирования и геймдизайна. Мы верим, что каждый молодой
              человек может стать создателем цифровых миров, и даём для этого все возможности.
            </p>

            <h2>Цели чемпионата</h2>
            <ul>
              <li>Создать среду для профессионального самоопределения подростков.</li>
              <li>Сформировать кадровый резерв для креативной экономики региона.</li>
              <li>Популяризировать креативные индустрии как карьерную траекторию.</li>
              <li>Обеспечить взаимодействие между участниками, экспертами и бизнесом.</li>
            </ul>

            <h2>Этапы чемпионата (детально)</h2>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-2xl font-bold text-primary-700">1. Индивидуальный отборочный этап</h3>
              <p className="text-gray-700">
                <strong>Даты:</strong> 31 октября – 2 ноября 2026<br />
                <strong>Формат:</strong> Онлайн-тестирование + практическое задание по выбранной компетенции.<br />
                <strong>Результат:</strong> 50 лучших участников проходят в следующий этап.
              </p>

              <h3 className="text-2xl font-bold text-primary-700 mt-8">2. Этап командообразования</h3>
              <p className="text-gray-700">
                <strong>Даты:</strong> 10–20 ноября 2026<br />
                <strong>Формат:</strong> Онлайн-встречи с менторами, формирование команд, выбор темы финального проекта.<br />
                <strong>Результат:</strong> Сформированные команды по 3–5 человек.
              </p>

              <h3 className="text-2xl font-bold text-primary-700 mt-8">3. Командный соревновательный этап (финал)</h3>
              <p className="text-gray-700">
                <strong>Даты:</strong> 21–30 ноября 2026<br />
                <strong>Формат:</strong> Очно-заочный хакатон. Команды работают над проектом при поддержке менторов.<br />
                <strong>Результат:</strong> Разработка прототипа проекта.
              </p>

              <h3 className="text-2xl font-bold text-primary-700 mt-8">4. Церемония награждения</h3>
              <p className="text-gray-700">
                <strong>Даты:</strong> 5–9 декабря 2026<br />
                <strong>Формат:</strong> Очная церемония в Ханты-Мансийске с онлайн-трансляцией.<br />
                <strong>Результат:</strong> ащита проектов перед жюри, определение победителей. Вручение призов, грантов, объявление победителей.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="card">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Ключевые особенности</h3>
            <div className="space-y-6">
              {features.map((feat) => (
                <div key={feat.title} className="flex items-start space-x-4">
                  <div className="p-3 bg-primary-100 rounded-lg">
                    <feat.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{feat.title}</h4>
                    <p className="text-gray-600 text-sm mt-1">{feat.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Основные правила</h3>
            <ul className="space-y-3">
              {rules.map((rule, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="h-2 w-2 rounded-full bg-primary-500 mt-2 mr-3" />
                  <span className="text-gray-700">{rule}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <a
                href="/documents/regulation/polozhenie_2026-2027.pdf"
                className="btn-primary w-full inline-flex items-center justify-center"
              >
                Скачать полное положение (PDF)
              </a>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-primary-50 to-accent-50">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Организатор</h3>
            <p className="text-gray-700">
              <strong>АНО ДПО «Институт развития компетенций»</strong> — региональная образовательная организация, оператор проектных смен и чемпионатов, исполнитель общественно полезных услуг.
            </p>
            <div className="mt-6">
              <a
                href="https://ircomp.ru"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 font-semibold hover:underline"
              >
                Перейти на сайт организатора →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}